import { t } from '@hefe/i18n';
import { Prospect, RequestBasePostProspectReply } from '@hefe/models';
import {
  BasketballTeamsSlice,
  ModalsSlice,
  ProspectsApi,
  currentUserIsAdminSelector,
} from '@hefe/store';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomForm from '../../../core/components/form/custom-form';

interface ProspectReplyFormProps {
  prospect: Prospect;
  teamId?: number;
}

export default function ProspectReplyForm(props: ProspectReplyFormProps) {
  const { prospect, teamId } = props;

  const dispatch = useAppDispatch();

  const userIsAdmin = useSelector(currentUserIsAdminSelector);
  const teams = useSelector(
    BasketballTeamsSlice.basketballTeamsSelectWithoutPendingSelector
  );

  const [replyToProspect, { isLoading }] =
    ProspectsApi.useReplyToProspectMutation();

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = () => {
    dispatch(
      BasketballTeamsSlice.fetchBasketballTeamsSelect({
        cacheDuration: 5 * 60 * 1000,
      })
    );
  };

  const initialTeamId = useMemo(() => {
    if (prospect.basketball_teams && prospect.basketball_teams.length > 0) {
      return prospect.basketball_teams[0].id;
    } else {
      return teamId;
    }
  }, [prospect.basketball_teams, teamId]);

  const filteredTeams = useMemo(() => {
    if (userIsAdmin) {
      return teams;
    } else {
      return teams.filter((team) => team.value === teamId);
    }
  }, [teams, teamId, userIsAdmin]);

  const onSubmit = (values: any, resetFields: any) => {
    const messageWithoutTags = (values?.message ?? '')
      .replace(/<[^>]*>?/gm, '')
      .trim();
    const submitData: RequestBasePostProspectReply = {
      status: values?.reply,
      message: messageWithoutTags.length === 0 ? undefined : values?.message,
      groupable_type: 'App\\Models\\Basketball\\BasketballTeam',
      groupable_id: values.team_id,
    };

    replyToProspect({ prospectId: prospect.id, request: submitData })
      .unwrap()
      .then(() => {
        resetFields();
        dispatch(ModalsSlice.modalsActions.closeModal());
      });
  };

  return (
    <CustomForm
      key={`prospect-reply-form-${prospect.id}`}
      submitButtonText={t('prospects.replies.singular')}
      loading={isLoading}
      fields={[
        {
          name: 'reply',
          type: 'radiogroup',
          label: t('prospects.replies.singular'),
          placeholder: t('prospects.replies.singular'),
          selectOptions: [
            { label: t('prospects.replies.approve'), value: 'approved' },
            { label: t('prospects.replies.decline'), value: 'declined' },
            { label: t('prospects.replies.waitlist'), value: 'waitlist' },
          ],
          rules: [
            {
              required: true,
              type: 'string',
              message: t('form_errors.value'),
            },
          ],
        },
        {
          name: 'team_id',
          type: 'select',
          label: t('team'),
          placeholder: t('select'),
          selectOptions: filteredTeams,
          initalValue: initialTeamId,
          disableSelectClear: true,
          rules: [
            {
              required: true,
              type: 'number',
              message: t('form_errors.value'),
            },
          ],
        },
        {
          name: 'message',
          type: 'texteditor',
          label: t('prospects.message'),
          placeholder: t('prospects.message'),
          rules: [],
        },
      ]}
      onSubmit={onSubmit}
    />
  );
}
